import { useLayoutEffect, useRef, useState } from "react";

export function findPhotoScrollRoot(node: HTMLElement): HTMLElement | null {
  let parent = node.parentElement;
  while (parent && parent !== document.body && parent !== document.documentElement) {
    const style = getComputedStyle(parent);
    if (parent.hasAttribute("data-radix-scroll-area-viewport") || /(auto|scroll)/.test(`${style.overflowY} ${style.overflowX}`)) return parent;
    parent = parent.parentElement;
  }
  return null;
}

/** No browser lazy-loading dependency. Measure on mount, then observe the actual gallery viewport. */
export function usePhotoTileVisibility() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const root = findPhotoScrollRoot(node);
    const check = () => {
      const box = node.getBoundingClientRect();
      const bounds = root?.getBoundingClientRect();
      const top = Math.max(0, bounds?.top ?? 0);
      const bottom = Math.min(window.innerHeight, bounds?.bottom ?? window.innerHeight);
      const left = Math.max(0, bounds?.left ?? 0);
      const right = Math.min(window.innerWidth, bounds?.right ?? window.innerWidth);
      // Zero-sized hidden tabs must not start work. jsdom has no layout: tests supply geometry.
      setVisible(box.width > 0 && box.height > 0 && box.bottom >= top - 100 && box.top <= bottom + 100 && box.right >= left && box.left <= right);
    };
    check();
    let frame = requestAnimationFrame(check); // Radix can finish layout after mounting the portal.
    const onMove = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(check); };
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(() => check(), { root, rootMargin: "100px 0px" });
    observer?.observe(node);
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(onMove);
    resize?.observe(node);
    if (root) resize?.observe(root);
    (root ?? window).addEventListener("scroll", onMove, { passive: true });
    window.addEventListener("resize", onMove);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      resize?.disconnect();
      (root ?? window).removeEventListener("scroll", onMove);
      window.removeEventListener("resize", onMove);
    };
  }, []);
  return { ref, visible };
}